// API Configuration
// This file centralizes all API endpoint configuration

import axios from 'axios';
import { invalidateDashboardCache } from './dashboardCache';
import { logoutToLogin } from '../utils/authExperience';

// For Vercel deployment and local dev with Vercel CLI, use relative paths (same domain)
// This works both locally (with `vercel dev`) and in production
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export function authHeader() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    const token = user?.token ?? (user?.user && user.user.token);
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch (_) {
    return {};
  }
}

function clearAuthHeader(config) {
  const h = config.headers;
  if (!h) return;
  if (typeof h.delete === 'function') h.delete('Authorization');
  delete h.Authorization;
}

// Request interceptor to add auth token if available
api.interceptors.request.use(
  (config) => {
    try {
      // Only for truly public reads (e.g. marketing home). Never set for all GET /api/properties - breaks agent dashboards.
      if (config.publicRequest === true) {
        clearAuthHeader(config);
        return config;
      }
      // Caller set Authorization explicitly (e.g. demo bar refreshing roster with admin JWT).
      if (config.preserveAuthHeader && config.headers?.Authorization) {
        return config;
      }
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      const token = user?.token ?? (user?.user && user.user.token);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      if (config.data instanceof FormData) {
        delete config.headers['Content-Type'];
      }
    } catch (err) {
      if (!config.quietErrors) console.warn('Error parsing user from localStorage:', err);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

function isWorkbenchLivePreviewHost() {
  if (typeof window === 'undefined') return false;
  const host = String(window.location.hostname || '').toLowerCase();
  const productHost = host === 'www.internationalpropertymarket.com'
    || host === 'internationalpropertymarket.com'
    || host === 'localhost'
    || host === '127.0.0.1';
  // Laptop IAP tunnel only. :3001 local prod and www keep the normal 401 logout.
  if (productHost) return window.location.port === '3011';
  if (window.location.port === '3011') return true;
  if (host.includes('ipm-workbench-preview')) return true;
  try {
    if (document.getElementById('ipm-preview-chrome')) return true;
  } catch (_) { /* ignore */ }
  return false;
}

// Only these bodies mean the IPM login itself is missing or rejected.
// A mailbox, DocuSign, portal, or webhook 401 must not send anyone to /login.
const SESSION_FAILURE_MESSAGES = new Set([
  'authorization required',
  'authentication required',
  'invalid token',
  'invalid or expired token',
  'invalid session',
  'no token provided',
  'user not found',
  'unauthorized',
]);

function isIpmSessionFailure(error) {
  if (error.response?.status !== 401) return false;
  if (error.response?.data?.needsReconnect === true) return false;
  const data = error.response?.data;
  const raw = data && typeof data === 'object' ? (data.message || data.error || '') : '';
  const msg = String(raw).trim().toLowerCase();
  return SESSION_FAILURE_MESSAGES.has(msg);
}

// Response interceptor: on a real session failure, clear session and redirect to login
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const isLoginRequest = error.config?.url?.includes('/api/auth/login');
    const isAccountInvite = error.config?.url?.includes('/api/auth/account-invite');
    // GCP Live preview (hosted HTTPS or :3011). Never wipe the session here.
    // Tunnel blips, API restarts, and proxy 401s were sending QA to /login?session=expired.
    // www / localhost stay on the normal logout path.
    if (isWorkbenchLivePreviewHost()) {
      if (status === 401 && !isLoginRequest && !error.config?.quietErrors) {
        console.warn('Preview API 401 (session kept):', error.config?.url, error.response?.data?.message || error.message);
      }
      return Promise.reject(error);
    }
    if (isIpmSessionFailure(error) && !isLoginRequest && !isAccountInvite && !error.config?.skipAuthRedirectOn401) {
      if (!error.config?._demoTokenRetried) {
        try {
          const { getDemoState, ensureFreshDemoToken } = await import('../components/DemoModeBar');
          if (getDemoState()) {
            const refreshed = await ensureFreshDemoToken();
            if (refreshed) {
              error.config._demoTokenRetried = true;
              const user = JSON.parse(localStorage.getItem('user') || 'null');
              if (user?.token) {
                error.config.headers = error.config.headers || {};
                error.config.headers.Authorization = `Bearer ${user.token}`;
              }
              return api.request(error.config);
            }
          }
        } catch (_) { /* fall through to logout */ }
      }
      try {
        const { clearDemoSession } = await import('../components/DemoModeBar');
        clearDemoSession();
      } catch (_) {}
      try {
        const user = JSON.parse(localStorage.getItem('user') || 'null');
        if (user?._id) invalidateDashboardCache(user._id);
      } catch (_) {}
      const loginPath = logoutToLogin({ fallback: '/login' });
      window.location.href = `${loginPath}?session=expired`;
      return Promise.reject(error);
    }
    if (!error.config?.quietErrors) console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export default api;
export { API_BASE_URL };
